import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Check, CheckCircle2, Quote, ScanLine } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, SubmitButton, CtaBand, usePageMeta } from "@/components/shared";
import { api, submitLead, formatApiError } from "@/lib/api";
import { GUIDES, OWNER_SERIES_BUNDLE } from "@/lib/guides";
import { Stars } from "@/pages/Reviews";

const ORDERABLE = [...GUIDES, OWNER_SERIES_BUNDLE];

const GuideCard = ({ g, i, choose }) => (
  <Reveal key={g.slug} delay={i * 0.08} className="h-full">
    <div data-testid={`guide-card-${g.slug}`} className="flex h-full flex-col border border-navy/10 bg-white">
      <div className="img-frame overflow-hidden border-b border-navy/10">
        <img src={g.cover} alt={`${g.title} — ebook cover`} className="aspect-[3/4] w-full object-cover transition-transform duration-700 hover:scale-[1.03]" />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-navy/45">{g.series} · {g.pages} pages</p>
        <h3 className="mt-2 font-serif text-xl leading-snug text-navy">{g.title}</h3>
        <p className="mt-2 flex-1 text-[13px] leading-relaxed text-navy/60">{g.description}</p>
        <div className="mt-5 flex items-center justify-between gap-3">
          <span className="font-serif text-3xl font-light text-navy" data-testid={`guide-price-${g.slug}`}>${g.price}</span>
          <button onClick={() => choose(g.slug)} data-testid={`guide-buy-${g.slug}`} className="bg-navy px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark">
            Get It
          </button>
        </div>
      </div>
    </div>
  </Reveal>
);

export default function Guides() {
  usePageMeta(
    "Digital Guides — The Bermuda Owner Series",
    "Practical ebooks for Bermuda vacation-rental owners: The Blueprint ($30), Cleaning & Turnover Manual, Design Guide and 5-Star Guest Experience workbooks ($20 each), or the complete series at $79. Instant PDF download."
  );
  const [params] = useSearchParams();
  const buyParam = params.get("buy");
  const [selected, setSelected] = useState(
    buyParam && ORDERABLE.some((g) => g.slug === buyParam) ? buyParam : ORDERABLE[0].slug
  );
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [upgradeFrom, setUpgradeFrom] = useState(null);
  const orderRef = useRef(null);

  useEffect(() => {
    if (buyParam && ORDERABLE.some((g) => g.slug === buyParam)) {
      orderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/reviews", { params: { service: "The Bermuda Vacation Rental Blueprint" } })
      .then((res) => setReviews(res.data))
      .catch(() => {});
  }, []);

  const current = ORDERABLE.find((g) => g.slug === selected);
  const ownerGuides = GUIDES.filter((g) => g.collection !== "guest");
  const guestGuides = GUIDES.filter((g) => g.collection === "guest");
  const isUpgrade = Boolean(upgradeFrom) && selected === OWNER_SERIES_BUNDLE.slug;
  const effectivePrice = isUpgrade ? OWNER_SERIES_BUNDLE.price - upgradeFrom.price : current.price;

  const choose = (slug) => {
    setSelected(slug);
    setUpgradeFrom(null);
    setDone(false);
    orderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const startUpgrade = () => {
    setUpgradeFrom({ title: current.title, price: current.price });
    setSelected(OWNER_SERIES_BUNDLE.slug);
    setDone(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const product = isUpgrade ? `The Complete Owner Series — Upgrade (already own: ${upgradeFrom.title})` : current.title;
      await submitLead("ebook_order", { ...form, product, slug: current.slug, price: `$${effectivePrice}` });
      setDone(true);
      setUpgradeFrom(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="guides-page">
      <section className="bg-sand-light">
        <div className="mx-auto max-w-[1440px] px-6 pb-20 pt-40 lg:px-10">
          <SectionHead
            overline="The Bermuda Owner Series · Instant Downloads"
            title={<>Digital guides,<br /><span className="italic text-bermuda-dark">written for Bermuda.</span></>}
            copy="Not generic hosting advice. Every guide is built from real Bermuda vacation-rental operations — practical, fillable and yours to keep. PDF download, delivered to your inbox."
            align="center"
          />
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-10">
        <div className="mb-10 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda-dark">For Owners & Hosts</p>
          <h3 className="mt-3 font-serif text-2xl font-light text-navy">The Bermuda Owner Series</h3>
        </div>
        <div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-4">
          {ownerGuides.map((g, i) => <GuideCard key={g.slug} g={g} i={i} choose={choose} />)}
        </div>

        <Reveal delay={0.1}>
          <div className="noise-overlay mt-16 grid items-center gap-8 bg-navy p-10 text-ivory lg:grid-cols-12 lg:p-14" data-testid="bundle-card">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda">Best Value</p>
              <h3 className="mt-4 font-serif text-3xl font-light leading-tight lg:text-4xl">The Complete Owner Series.</h3>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-ivory/70">
                All four ebooks — the flagship Blueprint plus the Cleaning &amp; Turnover Manual, the Design Guide and the
                5-Star Guest Experience workbook. The whole library in one order.
              </p>
              <ul className="mt-6 grid gap-2.5 text-[13px] text-ivory/85 sm:grid-cols-2">
                {ownerGuides.map((g) => (
                  <li key={g.slug} className="flex items-start gap-2.5">
                    <Check size={14} strokeWidth={2.5} className="mt-0.5 shrink-0 text-bermuda" /> {g.title}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <p className="text-[11px] uppercase tracking-[0.2em] text-ivory/50">All 4 ebooks</p>
              <p className="mt-2 font-serif text-5xl font-light text-ivory" data-testid="bundle-price">${OWNER_SERIES_BUNDLE.price}</p>
              <p className="mt-2 text-xs leading-relaxed text-ivory/50">Worth ${OWNER_SERIES_BUNDLE.worth} bought separately — you save ${OWNER_SERIES_BUNDLE.worth - OWNER_SERIES_BUNDLE.price}.</p>
              <button
                onClick={() => choose(OWNER_SERIES_BUNDLE.slug)}
                data-testid="bundle-buy-btn"
                className="mt-6 inline-flex items-center gap-2.5 bg-bermuda px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory"
              >
                Get the Bundle — ${OWNER_SERIES_BUNDLE.price}
              </button>
            </div>
          </div>
        </Reveal>

        <div className="mb-10 mt-20 text-center" data-testid="guest-series-head">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda-dark">For Guests</p>
          <h3 className="mt-3 font-serif text-2xl font-light text-navy">The Bermuda Guest Series</h3>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-navy/60">
            Fillable planners for every kind of stay — plane arrivals, couples, families, luxury escapes and cruise port days.
            Each pairs with our free <Link to="/plan" className="font-medium text-bermuda-dark underline underline-offset-4 hover:text-navy">My Bermuda Stay</Link> planner.
          </p>
        </div>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="guest-guides-grid">
          {guestGuides.map((g, i) => <GuideCard key={g.slug} g={g} i={i} choose={choose} />)}
        </div>

        <div ref={orderRef} className="scroll-mt-28">
          <Reveal delay={0.15}>
            <div className="mx-auto mt-16 max-w-4xl border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-12" data-testid="guide-order">
              {done ? (
                <div className="py-6 text-center" data-testid="guide-order-success">
                  <CheckCircle2 size={40} strokeWidth={1.2} className="mx-auto text-bermuda-dark" />
                  <h3 className="mt-5 font-serif text-3xl text-navy">Order received.</h3>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-navy/60">
                    Thank you! As soon as your payment is confirmed, the download link for <strong className="text-navy">{current.title}</strong> will
                    arrive at <strong className="text-navy">{form.email}</strong> — usually the same day. Keep an eye on your inbox (and junk folder, just in case).
                  </p>
                  {current.slug !== OWNER_SERIES_BUNDLE.slug && (
                    <div className="mx-auto mt-8 max-w-md border border-bermuda/40 bg-pink-soft/40 p-6" data-testid="bundle-upgrade-offer">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-bermuda-dark">Thank-You Offer</p>
                      <p className="mt-2 text-[13px] leading-relaxed text-navy/70">
                        Love it? Add the other three guides whenever you're ready — upgrade to The Complete Owner Series
                        for just <strong className="text-navy">${OWNER_SERIES_BUNDLE.price - current.price}</strong>, the bundle difference. Nothing more.
                      </p>
                      <button onClick={startUpgrade} data-testid="bundle-upgrade-btn" className="mt-4 w-full bg-navy px-5 py-3.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark">
                        Upgrade for ${OWNER_SERIES_BUNDLE.price - current.price}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <h3 className="flex items-center gap-3 font-serif text-2xl text-navy"><BookOpen size={20} className="text-bermuda-dark" /> Order Your Ebook</h3>
                  <div className="mt-6">
                    <Field label="Choose your ebook" required>
                      <select
                        data-testid="guide-select"
                        value={selected}
                        onChange={(e) => { setSelected(e.target.value); if (e.target.value !== OWNER_SERIES_BUNDLE.slug) setUpgradeFrom(null); }}
                        className="w-full appearance-none border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy outline-none focus:border-bermuda-dark"
                      >
                        {ORDERABLE.map((g) => (
                          <option key={g.slug} value={g.slug}>{g.title} — ${g.price}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="mt-8 grid gap-8 sm:grid-cols-2">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60" data-testid="guide-pay-step">Step 1 — Pay ${effectivePrice}</p>
                      {isUpgrade && (
                        <p className="mt-2 border-l-2 border-bermuda pl-3 text-[12px] leading-relaxed text-navy/60" data-testid="upgrade-note">
                          Bundle upgrade — you already own {upgradeFrom.title}. This completes your set at the bundle difference.
                        </p>
                      )}
                      <p className="mt-2 text-[13px] leading-relaxed text-navy/60">Scan the QR code with your phone camera to pay. Add your email address as the payment reference.</p>
                      <div className="mt-4 inline-block border border-navy/10 bg-white p-3" data-testid="guide-order-qr">
                        <img src="/images/payment-qr.png" alt="Payment QR code — The Bermuda Co-Host" className="h-36 w-36" />
                      </div>
                      <p className="mt-3 flex items-center gap-2 text-xs text-navy/45"><ScanLine size={13} /> Works with any camera or payment app</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60">Step 2 — Where do we send it?</p>
                      <form onSubmit={onSubmit} className="mt-3 grid gap-4" data-testid="guide-order-form">
                        <Field label="Your Name" required><Input data-testid="guide-order-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field label="Email" required hint="Your guide is emailed here after your payment is confirmed"><Input data-testid="guide-order-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                        <SubmitButton loading={loading} testid="guide-order-submit">{isUpgrade ? `Complete My Upgrade — $${effectivePrice}` : `Send My Ebook — $${effectivePrice}`}</SubmitButton>
                      </form>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="mx-auto mt-16 max-w-4xl" data-testid="guides-reviews">
            <h3 className="font-serif text-2xl text-navy">Reader Reviews</h3>
            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-navy/45">The Bermuda Vacation Rental Blueprint</p>
            {reviews.length === 0 ? (
              <div className="mt-5 border border-dashed border-navy/20 bg-sand-light/60 p-8 text-center">
                <Quote size={20} strokeWidth={1.2} className="mx-auto text-navy/25" />
                <p className="mt-3 text-sm text-navy/50">
                  First readers are inside now. Read it, then{" "}
                  <Link to="/reviews?service=blueprint" data-testid="guides-review-link" className="text-bermuda-dark underline underline-offset-4 hover:text-navy">leave the first review</Link>.
                </p>
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                {reviews.map((r) => (
                  <div key={r.id} className="border border-navy/10 bg-white p-6" data-testid={`guides-review-${r.id}`}>
                    <Stars value={r.rating} />
                    <p className="mt-3 text-sm leading-relaxed text-navy/75">"{r.text}"</p>
                    <p className="mt-3 text-xs font-medium text-navy/55">— {r.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Reveal>
      </section>

      <CtaBand title="Prefer we apply it for you?" copy="The guides teach you how. Our team can simply do it — start with a free property assessment." />
    </main>
  );
}
